/**
 * useLiveProfileName — the user's REAL first + last name, live from the
 * profile page's own document (owner directive 2026-08-12).
 *
 * The fullscreen chat footer showed the email local-part ("Fernandohidalgoesq")
 * because it read AuthContext's `user.name`, a login-time snapshot that many
 * accounts seeded from their email. The profile page's source of truth is the
 * Firestore doc `users/{uid}/user_profile/user_profile` (array schema:
 * `profiles[0].first_name/last_name`, exactly what the profile form edits) —
 * this hook subscribes to THAT doc with onSnapshot, so a name edited on the
 * profile page reflects everywhere this hook is used in real time, no reload.
 *
 * Fail-open contract: no uid, no doc, blank names, or a subscription error all
 * resolve to null — callers keep their existing AuthContext fallback chain, so
 * this can only ever improve the displayed identity, never blank it.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '../utils/firestore';

export interface LiveProfileName {
  firstName: string;
  lastName: string;
  /** "First Last" (single-sided names collapse to the one present). */
  fullName: string;
}

/**
 * Pure extractor for the profile doc's array schema. Exported for unit tests
 * and any non-React consumer.
 */
export function extractProfileFullName(data: unknown): LiveProfileName | null {
  const profiles = (data as { profiles?: unknown } | null | undefined)?.profiles;
  if (!Array.isArray(profiles) || profiles.length === 0) return null;
  const p = (profiles[0] ?? {}) as Record<string, unknown>;
  const firstName = String(p.first_name ?? '').trim();
  const lastName = String(p.last_name ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  if (!fullName) return null;
  return { firstName, lastName, fullName };
}

export function useLiveProfileName(uid: string | null | undefined): LiveProfileName | null {
  const [name, setName] = useState<LiveProfileName | null>(null);

  useEffect(() => {
    const id = (uid ?? '').trim();
    if (!id) {
      setName(null);
      return undefined;
    }
    try {
      const ref = doc(db, 'users', id, 'user_profile', 'user_profile');
      const unsubscribe = onSnapshot(
        ref,
        (snap) => {
          setName(extractProfileFullName(snap.exists() ? snap.data() : null));
        },
        () => {
          // Permission/offline error — fall back to the caller's auth identity.
          setName(null);
        },
      );
      return unsubscribe;
    } catch {
      setName(null);
      return undefined;
    }
  }, [uid]);

  return name;
}

export default useLiveProfileName;
